import { type AiTagId, type SiteId } from '@solinkcorp/api-types';
import { useMemo } from 'react';
import usePeopleTrackingRawData from './usePeopleTrackingRawData';

/**
 * A real (non-simulated) zone-tracking zone read from the device's vision analytics
 * config, i.e. one entry of some camera's `dwell_event_polygons`. `polygonId` and `camId`
 * come straight from the device config, as opposed to the testing tab's own generated
 * `sim-cam-*` / `sim-polygon-*` ids.
 */
export interface RealZoneOption {
  camId: string;
  polygonId: string;
  label: string;
  zoneType: string;
  vertices: Array<[number, number]>;
  aiTagIds: AiTagId[];
}

/**
 * Flattens the site's line crossing configuration (with multicam) into the list of real
 * zones available for the testing tab to pick a camera id + zone id from when simulating
 * a zone-tracking event for a given AI tag. Returns [] when no device config is available
 * (e.g. vision analytics disabled, or no device reachable) so callers can fall back to a
 * fully-simulated zone context.
 */
export default function useRealZoneOptions(siteId: SiteId | string | undefined): RealZoneOption[] {
  const { rawData } = usePeopleTrackingRawData(siteId);

  return useMemo(() => {
    if (!rawData) return [];

    const zones: RealZoneOption[] = [];
    for (const [camId, cameraConfig] of Object.entries(rawData)) {
      for (const polygon of cameraConfig?.['dwell_event_polygons'] ?? []) {
        const polygonId = polygon?.metadata?.polygonId;
        if (!polygonId) continue;
        zones.push({
          camId,
          polygonId,
          label: polygon.label,
          zoneType: polygon.metadata?.zoneType ?? 'queue',
          vertices: polygon.vertices,
          aiTagIds: polygon.metadata?.aiTagIds ?? [],
        });
      }
    }
    return zones;
  }, [rawData]);
}
